// src/pages/Orders/components/BillExport.jsx
// Branded bill/invoice export modal — uses branchDisplay Redux state for company branding
// Usage: <BillExport order={order} onClose={() => setShowBill(false)} />

import { useRef, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { XIcon, PrinterIcon } from "@heroicons/react/outline";
import { BadgeCheckIcon } from "@heroicons/react/solid";
import { getBranchDisplayThunk } from "../../../store/thunks/branchDisplayThunks";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatCurrency = (val) =>
  val !== undefined && val !== null
    ? `₹${Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Status pill colours
const paymentStatusStyle = (status) => {
  const map = {
    PAID: { bg: "#d1fae5", text: "#065f46" },
    PARTIALLY_PAID: { bg: "#fef3c7", text: "#92400e" },
    UNPAID: { bg: "#fee2e2", text: "#991b1b" },
    PENDING: { bg: "#e0e7ff", text: "#3730a3" },
  };
  return map[status] || { bg: "#f3f4f6", text: "#374151" };
};

// ─── Main Component ──────────────────────────────────────────────────────────

export default function BillExport({ order, onClose }) {
  const printRef = useRef(null);
  const dispatch = useDispatch();

  // Pull branding from Redux
  const {
    display,
    contactInfo,
    address,
    currentBranchCode,
    loading: branchLoading,
  } = useSelector((s) => s.branchDisplay);

  // The branch this order belongs to
  const orderBranchCode =
    order?.branchCode || order?.fulfillment?.location?.branchCode;

  // Fetch branch display data if:
  //   • Redux has no data at all, OR
  //   • The loaded branch is different from the order's branch
  useEffect(() => {
    if (!orderBranchCode) return;
    const alreadyLoaded =
      currentBranchCode === orderBranchCode && display !== null;
    if (!alreadyLoaded) {
      dispatch(getBranchDisplayThunk(orderBranchCode));
    }
  }, [orderBranchCode, currentBranchCode, display, dispatch]);

  // ── Responsive breakpoints (live, handles resize + iPad) ─────────────────
  const [screenW, setScreenW] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );
  useEffect(() => {
    const onResize = () => setScreenW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = screenW < 480; // phones
  const isTablet = screenW < 900; // iPad, small laptops

  // ── Derived brand values ─────────────────────────────────────────────────
  const primary = display?.brandColors?.primary || "#D4AF37";
  const secondary = display?.brandColors?.secondary || "#1a1a2e";
  const accent = display?.brandColors?.accent || "#B8860B";
  const logoUrl = display?.logo?.url;
  const storeName = display?.displayName || "Jewellery Store";
  const tagline = display?.tagline || "";

  // Contact
  const phone = contactInfo?.phone?.primary || contactInfo?.phone?.sales || "";
  const email = contactInfo?.email?.primary || "";
  const website = contactInfo?.website?.url || "";
  const whatsapp = contactInfo?.whatsapp?.number || "";

  // Address
  const addrLine = [
    address?.line1,
    address?.line2,
    address?.city,
    address?.state,
    address?.pinCode,
  ]
    .filter(Boolean)
    .join(", ");

  // ── Derived order values ─────────────────────────────────────────────────
  const customerName =
    order?.userId?.name || order?.guestCustomer?.name || "Walk-in Customer";
  const customerPhone =
    order?.userId?.phoneNumber ||
    order?.userId?.phone ||
    order?.guestCustomer?.phone ||
    "";
  const customerEmail =
    order?.userId?.email || order?.guestCustomer?.email || "";

  const summary = order?.orderSummary || {};
  const items = order?.items || [];

  // Exchange: lives at order.exchangeItems[], accepted ones have agreedValue
  const exchangeItems = (order?.exchangeItems || []).filter(
    (ex) => ex.status === "ACCEPTED" || ex.customerAccepted === true,
  );
  const exchangeValue = summary?.finalTotals?.totalExchangeValue ?? 0;

  // Totals — use finalTotals, fall back to baseTotals
  const finalTotals = summary?.finalTotals || summary?.baseTotals || {};
  const grandTotal = finalTotals?.grandTotal ?? 0;
  const netPayable = finalTotals?.netPayable ?? grandTotal;
  const totalGST = finalTotals?.totalGST ?? 0;
  const totalDiscount = finalTotals?.totalDiscount ?? 0;
  const amountPaid = summary?.amountPaid ?? 0;
  const amountDue = summary?.amountDue ?? 0;

  // Aggregate item-level breakdown for totals panel
  const itemTotals = items.reduce(
    (acc, item) => {
      const bd =
        item.finalPricing?.breakdown || item.basePricing?.breakdown || {};
      acc.metalValue += bd.metalValue ?? 0;
      acc.makingCharges += bd.charges?.making ?? 0;
      acc.wastageCharges += bd.charges?.wastage ?? 0;
      acc.stoneCharges += bd.stoneCharges ?? 0;
      acc.subtotal += bd.subtotal ?? 0;
      return acc;
    },
    {
      metalValue: 0,
      makingCharges: 0,
      wastageCharges: 0,
      stoneCharges: 0,
      subtotal: 0,
    },
  );

  // Fulfillment info
  const fulfillmentType = order?.fulfillment?.type || "PICKUP";
  const fulfillmentBranch = order?.fulfillment?.location?.branchCode || "";

  const psStyle = paymentStatusStyle(order?.payment?.status);

  // ── Loading guard — wait for branch data if fetch is in progress ─────────
  const isBranchLoading = branchLoading && !display;

  // ── Print handler ─────────────────────────────────────────────────────────
  // We clone the bill paper directly onto <body> so it's not constrained by
  // the position:fixed overlay div (which is viewport-width, not A4-width).
  const handlePrint = () => {
    const billNode = printRef.current;
    if (!billNode) return;

    // Build a meaningful filename for the Save PDF dialog
    const orderId = order?.orderId || "";
    const custName = (customerName || "")
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9-]/g, "");
    const suggested = [orderId, custName].filter(Boolean).join("_");
    const prevTitle = document.title;
    document.title = suggested || prevTitle; // browser uses <title> as default PDF filename

    // 1. Deep-clone with all inline styles intact
    const clone = billNode.cloneNode(true);
    clone.id = "__bill_print_clone__";
    clone.removeAttribute("style");
    document.body.appendChild(clone);

    // 2. Inject print CSS targeting the clone
    const style = document.createElement("style");
    style.id = "__bill_print_css__";
    style.textContent = `
      @media print {
        @page { margin: 10mm; size: A4 portrait; }

        body > *:not(#__bill_print_clone__) {
          display: none !important;
        }

        #__bill_print_clone__ {
          display: block !important;
          position: static !important;
          width: 100% !important;
          padding: 0 !important;
          margin: 0 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        #__bill_print_clone__ * {
          overflow: visible !important;
          max-width: 100% !important;
        }

        #__bill_print_clone__ table {
          width: 100% !important;
          min-width: 0 !important;
          table-layout: fixed !important;
        }

        #__bill_print_clone__ th,
        #__bill_print_clone__ td {
          padding: 6px 8px !important;
          font-size: 11px !important;
          word-break: break-word !important;
          white-space: normal !important;
        }

        .no-print-hint { display: none !important; }
      }
    `;
    document.head.appendChild(style);

    // 3. Print
    window.print();

    // 4. Cleanup — 2.5s covers iOS/Android async print dialog
    setTimeout(() => {
      const c = document.getElementById("__bill_print_clone__");
      const s = document.getElementById("__bill_print_css__");
      if (c) document.body.removeChild(c);
      if (s) document.head.removeChild(s);
      document.title = prevTitle; // restore original tab title
    }, 2500);
  };

  // ── Styles ───────────────────────────────────────────────────────────────
  const css = {
    overlay: {
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      background: "rgba(0,0,0,0.72)",
      backdropFilter: "blur(6px)",
      display: "flex",
      alignItems: isMobile ? "flex-end" : "center",
      justifyContent: "center",
      padding: isMobile ? "0" : "16px",
      fontFamily: "'DM Sans', sans-serif",
    },
    wrapper: {
      position: "relative",
      width: "100%",
      maxWidth: isMobile ? "100%" : isTablet ? "100%" : "820px",
      maxHeight: isMobile ? "94vh" : "92vh",
      overflowY: "auto",
      borderRadius: isMobile ? "16px 16px 0 0" : "4px",
    },
    actionBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      background: secondary,
      padding: isMobile ? "10px 14px" : "10px 16px",
      borderRadius: isMobile ? "16px 16px 0 0" : "4px 4px 0 0",
      position: "sticky",
      top: 0,
      zIndex: 10,
      gap: "8px",
    },
    paper: {
      background: "#fff",
      padding: isMobile
        ? "14px 14px 28px"
        : isTablet
          ? "24px 28px 36px"
          : "40px 48px",
      lineHeight: 1.5,
    },
    headerRow: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "12px",
      marginBottom: isMobile ? "14px" : "24px",
      paddingBottom: isMobile ? "14px" : "20px",
      borderBottom: `3px solid ${primary}`,
      flexWrap: "nowrap",
    },
    brandBlock: {
      display: "flex",
      alignItems: "flex-start",
      gap: "10px",
      flex: "1 1 0",
      minWidth: 0,
      overflow: "hidden",
    },
    brandText: { flex: "1 1 0", minWidth: 0, overflow: "hidden" },
    logo: {
      width: isMobile ? "36px" : isTablet ? "48px" : "60px",
      height: isMobile ? "36px" : isTablet ? "48px" : "60px",
      objectFit: "contain",
      flexShrink: 0,
    },
    logoPlaceholder: {
      width: isMobile ? "34px" : isTablet ? "44px" : "58px",
      height: isMobile ? "34px" : isTablet ? "44px" : "58px",
      background: primary,
      borderRadius: "2px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: isMobile ? "14px" : isTablet ? "18px" : "22px",
      fontWeight: 700,
      color: "#fff",
      fontFamily: "'Cormorant Garamond', serif",
      flexShrink: 0,
    },
    storeName: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: isMobile ? "16px" : isTablet ? "20px" : "26px",
      fontWeight: 700,
      color: secondary,
      lineHeight: 1.15,
      letterSpacing: "-0.3px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    storeTagline: {
      fontSize: isMobile ? "9px" : "10px",
      color: "#888",
      letterSpacing: "0.4px",
      marginTop: "2px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    invoiceTitle: { textAlign: "right", flexShrink: 0 },
    invoiceLabel: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: isMobile ? "20px" : isTablet ? "26px" : "32px",
      fontWeight: 700,
      color: primary,
      letterSpacing: "2px",
      textTransform: "uppercase",
    },
    orderId: {
      fontSize: isMobile ? "10px" : "12px",
      color: "#555",
      marginTop: "3px",
      fontWeight: 500,
      whiteSpace: "nowrap",
    },
    orderDate: {
      fontSize: isMobile ? "9px" : "11px",
      color: "#888",
      marginTop: "2px",
      whiteSpace: "nowrap",
    },
    statusPill: {
      display: "inline-block",
      marginTop: "5px",
      padding: "2px 8px",
      borderRadius: "20px",
      fontSize: "10px",
      fontWeight: 600,
      letterSpacing: "0.4px",
      background: psStyle.bg,
      color: psStyle.text,
      whiteSpace: "nowrap",
    },
    addressBand: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap: isMobile ? "10px" : "16px",
      background: "#fafafa",
      border: "1px solid #f0f0f0",
      borderLeft: `4px solid ${primary}`,
      padding: isMobile ? "10px 12px" : "14px 18px",
      marginBottom: isMobile ? "14px" : "20px",
      borderRadius: "0 2px 2px 0",
    },
    addrLabel: {
      fontSize: "9px",
      fontWeight: 600,
      color: primary,
      letterSpacing: "1.2px",
      textTransform: "uppercase",
      marginBottom: "4px",
    },
    addrValue: {
      fontSize: isMobile ? "11px" : "12px",
      color: "#333",
      lineHeight: 1.6,
    },
    tableWrapper: {
      overflowX: "auto",
      marginBottom: isMobile ? "14px" : "20px",
      WebkitOverflowScrolling: "touch",
      borderRadius: "2px",
      // Note: @media print CSS above resets overflow on the container
    },
    table: {
      width: "100%",
      minWidth: 0,
      tableLayout: "fixed",
      borderCollapse: "collapse",
      fontSize: isMobile ? "11px" : isTablet ? "12px" : "12.5px",
    },
    th: {
      background: secondary,
      color: "#fff",
      padding: isMobile ? "7px 9px" : "9px 12px",
      textAlign: "left",
      fontSize: isMobile ? "8.5px" : "9.5px",
      fontWeight: 600,
      letterSpacing: "0.7px",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    },
    thRight: {
      background: secondary,
      color: "#fff",
      padding: isMobile ? "7px 9px" : "9px 12px",
      textAlign: "right",
      fontSize: isMobile ? "8.5px" : "9.5px",
      fontWeight: 600,
      letterSpacing: "0.7px",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    },
    td: {
      padding: isMobile ? "7px 9px" : "9px 12px",
      borderBottom: "1px solid #f0f0f0",
      color: "#333",
      verticalAlign: "top",
      wordBreak: "break-word",
    },
    tdRight: {
      padding: isMobile ? "7px 9px" : "9px 12px",
      borderBottom: "1px solid #f0f0f0",
      color: "#333",
      textAlign: "right",
      verticalAlign: "top",
      wordBreak: "break-word",
    },
    tdMeta: { fontSize: "10px", color: "#888", marginTop: "2px" },
    totalsRow: {
      display: "flex",
      justifyContent: isMobile ? "stretch" : "flex-end",
      marginBottom: isMobile ? "14px" : "20px",
    },
    totalsBox: { width: isMobile ? "100%" : "280px" },
    totalLine: {
      display: "flex",
      justifyContent: "space-between",
      padding: "5px 0",
      fontSize: isMobile ? "11.5px" : "12.5px",
      color: "#444",
      borderBottom: "1px dashed #eee",
    },
    totalLineGrand: {
      display: "flex",
      justifyContent: "space-between",
      padding: "9px 12px",
      background: secondary,
      color: "#fff",
      fontSize: isMobile ? "13px" : "14px",
      fontWeight: 600,
      marginTop: "4px",
      borderRadius: "2px",
    },
    totalLineDue: {
      display: "flex",
      justifyContent: "space-between",
      padding: "8px 12px",
      background: amountDue > 0 ? "#fee2e2" : "#d1fae5",
      color: amountDue > 0 ? "#991b1b" : "#065f46",
      fontSize: isMobile ? "12px" : "13px",
      fontWeight: 600,
      borderRadius: "2px",
      marginTop: "4px",
    },
    exchangeBox: {
      background: "#fffbeb",
      border: "1px solid #fde68a",
      borderLeft: `4px solid ${accent}`,
      borderRadius: "0 2px 2px 0",
      padding: isMobile ? "9px 11px" : "12px 16px",
      marginBottom: isMobile ? "14px" : "20px",
      fontSize: isMobile ? "11px" : "12px",
    },
    exchLabel: {
      fontWeight: 600,
      color: accent,
      marginBottom: "5px",
      fontSize: "11px",
    },
    paymentGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: isMobile ? "6px" : "12px",
      background: "#f9fafb",
      padding: isMobile ? "10px" : "14px 16px",
      border: "1px solid #e5e7eb",
      borderRadius: "2px",
      marginBottom: isMobile ? "14px" : "20px",
      fontSize: isMobile ? "10.5px" : "12px",
    },
    payCell: { display: "flex", flexDirection: "column", gap: "2px" },
    payCellLabel: {
      fontSize: "8px",
      fontWeight: 600,
      color: "#999",
      letterSpacing: "0.7px",
      textTransform: "uppercase",
    },
    payCellValue: {
      fontSize: isMobile ? "12px" : "14px",
      fontWeight: 600,
      color: secondary,
    },
    footer: {
      borderTop: `2px solid ${primary}`,
      paddingTop: "12px",
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      justifyContent: "space-between",
      alignItems: isMobile ? "flex-start" : "flex-end",
      gap: isMobile ? "8px" : "0",
      fontSize: "11px",
      color: "#777",
    },
    footerContact: { lineHeight: 1.8 },
    footerThank: {
      textAlign: isMobile ? "left" : "right",
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: isMobile ? "13px" : "15px",
      color: primary,
      fontStyle: "italic",
    },
    watermarkRow: {
      textAlign: "center",
      marginTop: "12px",
      paddingTop: "10px",
      borderTop: "1px dashed #e5e7eb",
      fontSize: "9.5px",
      color: "#bbb",
      letterSpacing: "0.4px",
    },
    certRow: {
      display: "flex",
      gap: "5px",
      flexWrap: "wrap",
      marginTop: "4px",
    },
    certBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "3px",
      background: "#fefce8",
      border: `1px solid ${primary}40`,
      borderRadius: "3px",
      padding: "2px 6px",
      fontSize: "9px",
      color: accent,
      fontWeight: 600,
    },
  };

  const certifications =
    display?.certifications?.filter((c) => c.isVisible) || [];

  return (
    <>
      <div style={css.overlay}>
        {/* ── Loading state while branch data is being fetched ── */}
        {isBranchLoading ? (
          <div
            style={{
              background: "#fff",
              borderRadius: "4px",
              padding: "48px 64px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: `3px solid ${primary}30`,
                borderTop: `3px solid ${primary}`,
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
              Loading branch details…
            </p>
            <button
              onClick={onClose}
              style={{
                fontSize: "12px",
                color: "#999",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div style={css.wrapper}>
            {/* Action bar */}
            <div style={css.actionBar} className="no-print">
              <span
                style={{
                  color: "#fff",
                  fontSize: isMobile ? "12px" : "13px",
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "55%",
                }}
              >
                {isMobile ? order?.orderId : `Bill Preview — ${order?.orderId}`}
              </span>
              <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                <button
                  onClick={handlePrint}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    background: primary,
                    color: secondary,
                    border: "none",
                    borderRadius: "2px",
                    padding: isMobile ? "7px 10px" : "7px 14px",
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  <PrinterIcon style={{ width: 14, height: 14 }} />
                  {isMobile ? "PDF" : "Print / Save PDF"}
                </button>
                <button
                  onClick={onClose}
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    border: "none",
                    borderRadius: "2px",
                    padding: "7px 10px",
                    cursor: "pointer",
                    color: "#fff",
                  }}
                >
                  <XIcon style={{ width: 16, height: 16 }} />
                </button>
              </div>
            </div>

            {/* ── Bill Paper ─────────────────────────────────────────── */}
            <div
              ref={printRef}
              id="__bill_paper__"
              style={css.paper}
              className="bill-paper"
            >
              {/* Header */}
              <div style={css.headerRow}>
                {/* Brand */}
                <div style={css.brandBlock}>
                  {logoUrl ? (
                    <img src={logoUrl} alt={storeName} style={css.logo} />
                  ) : (
                    <div style={css.logoPlaceholder}>{storeName[0]}</div>
                  )}
                  <div style={css.brandText}>
                    <div style={css.storeName}>{storeName}</div>
                    {tagline && <div style={css.storeTagline}>{tagline}</div>}
                    {certifications.length > 0 && (
                      <div style={css.certRow}>
                        {certifications.map((c, i) => (
                          <span key={i} style={css.certBadge}>
                            <BadgeCheckIcon style={{ width: 11, height: 11 }} />
                            {c.name || c.type}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Invoice meta */}
                <div style={css.invoiceTitle}>
                  <div style={css.invoiceLabel}>Invoice</div>
                  <div style={css.orderId}># {order?.orderId || "—"}</div>
                  <div style={css.orderDate}>
                    {formatDateTime(order?.createdAt)}
                  </div>
                  {order?.payment?.status && (
                    <span style={css.statusPill}>{order.payment.status}</span>
                  )}
                </div>
              </div>

              {/* Address band: Store ↔ Customer */}
              <div style={css.addressBand}>
                {/* Store address */}
                <div>
                  <div style={css.addrLabel}>From</div>
                  <div style={{ ...css.addrValue, fontWeight: 500 }}>
                    {storeName}
                  </div>
                  {addrLine && <div style={css.addrValue}>{addrLine}</div>}
                  {phone && <div style={css.addrValue}>📞 {phone}</div>}
                  {email && <div style={css.addrValue}>✉ {email}</div>}
                  {website && <div style={css.addrValue}>🌐 {website}</div>}
                </div>
                {/* Customer */}
                <div>
                  <div style={css.addrLabel}>Bill To</div>
                  <div style={{ ...css.addrValue, fontWeight: 500 }}>
                    {customerName}
                  </div>
                  {customerPhone && (
                    <div style={css.addrValue}>📞 {customerPhone}</div>
                  )}
                  {customerEmail && (
                    <div style={css.addrValue}>✉ {customerEmail}</div>
                  )}
                  {fulfillmentBranch && (
                    <div
                      style={{
                        ...css.addrValue,
                        marginTop: "6px",
                        color: "#666",
                      }}
                    >
                      Branch: {fulfillmentBranch}
                    </div>
                  )}
                  <div style={{ ...css.addrValue, color: "#666" }}>
                    Fulfillment: {fulfillmentType}
                  </div>
                </div>
              </div>

              {/* Items table */}
              <div style={css.tableWrapper}>
                {isMobile && (
                  <div
                    className="no-print-hint"
                    style={{
                      fontSize: "10px",
                      color: "#aaa",
                      marginBottom: "4px",
                      textAlign: "right",
                    }}
                  >
                    ← scroll to see all columns
                  </div>
                )}
                <table style={css.table}>
                  <colgroup>
                    <col style={{ width: "4%" }} />
                    <col style={{ width: "38%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "17%" }} />
                    <col style={{ width: "17%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ ...css.th, width: "32px" }}>#</th>
                      <th style={css.th}>Item Description</th>
                      <th style={css.th}>Purity / Weight</th>
                      <th style={{ ...css.thRight, width: "60px" }}>Qty</th>
                      <th style={{ ...css.thRight, width: "100px" }}>
                        Unit Price
                      </th>
                      <th style={{ ...css.thRight, width: "110px" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          style={{
                            ...css.td,
                            textAlign: "center",
                            color: "#999",
                            padding: "20px",
                          }}
                        >
                          No items
                        </td>
                      </tr>
                    ) : (
                      items.map((item, i) => {
                        const snap = item.productSnapshot || {};
                        const price =
                          item.finalPricing || item.basePricing || {};
                        const purity = snap.metalDetails?.purity || "";
                        const weight = snap.grossWeight ?? snap.netWeight;
                        const breakdown = price.breakdown || {};
                        const qty = item.quantity || 1;

                        // Correct unit price = subtotal (metal+making+wastage+additional) + GST
                        // breakdown.finalAmount excludes charges.additional so we don't use it
                        const itemSubtotal = breakdown.subtotal ?? 0;
                        const itemGST = breakdown.gst?.amount ?? 0;
                        const unitPrice =
                          itemSubtotal > 0
                            ? (itemSubtotal + itemGST) / qty
                            : (price.unitPrice ?? null);
                        const lineTotal =
                          unitPrice != null
                            ? unitPrice * qty
                            : (price.totalPrice ?? null);

                        const makingCharges = breakdown.charges?.making ?? 0;
                        const wastageCharges = breakdown.charges?.wastage ?? 0;
                        const additionalCharges =
                          breakdown.charges?.additional ?? 0;
                        const stoneCharges = breakdown.stoneCharges ?? 0;

                        return (
                          <tr
                            key={i}
                            style={{
                              background: i % 2 === 0 ? "#fff" : "#fafafa",
                            }}
                          >
                            <td
                              style={{
                                ...css.td,
                                color: "#bbb",
                                fontSize: "11px",
                              }}
                            >
                              {i + 1}
                            </td>
                            <td style={css.td}>
                              <div style={{ fontWeight: 500 }}>
                                {snap.name || item.description || "Item"}
                              </div>
                              {snap.sku && (
                                <div style={css.tdMeta}>SKU: {snap.sku}</div>
                              )}
                              {breakdown.metalValue ? (
                                <div style={css.tdMeta}>
                                  Metal {formatCurrency(breakdown.metalValue)}
                                  {makingCharges > 0
                                    ? ` · Making ${formatCurrency(makingCharges)}`
                                    : ""}
                                  {wastageCharges > 0
                                    ? ` · Wastage ${formatCurrency(wastageCharges)}`
                                    : ""}
                                  {additionalCharges > 0
                                    ? ` · Other ${formatCurrency(additionalCharges)}`
                                    : ""}
                                  {stoneCharges > 0
                                    ? ` · Stone ${formatCurrency(stoneCharges)}`
                                    : ""}
                                  {itemGST > 0
                                    ? ` · GST ${formatCurrency(itemGST)}`
                                    : ""}
                                </div>
                              ) : null}
                            </td>
                            <td style={css.td}>
                              {purity && <div>{purity}</div>}
                              {weight && (
                                <div style={css.tdMeta}>{weight}g</div>
                              )}
                            </td>
                            <td style={css.tdRight}>{item.quantity ?? 1}</td>
                            <td style={css.tdRight}>
                              {formatCurrency(unitPrice)}
                            </td>
                            <td style={{ ...css.tdRight, fontWeight: 500 }}>
                              {formatCurrency(lineTotal)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {/* end tableWrapper */}

              {/* Exchange items */}
              {exchangeItems.length > 0 && (
                <div style={css.exchangeBox}>
                  <div style={css.exchLabel}>⇄ Exchange Items</div>
                  {exchangeItems.map((exItem, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "4px 0",
                        borderBottom: "1px dashed #fde68a",
                        fontSize: "12px",
                      }}
                    >
                      <span>
                        {exItem.description ||
                          exItem.itemType ||
                          "Exchange item"}{" "}
                        {exItem.netWeight
                          ? `· ${exItem.netWeight}g`
                          : exItem.grossWeight
                            ? `· ${exItem.grossWeight}g`
                            : ""}
                        {exItem.metalDetails?.purity
                          ? ` · ${exItem.metalDetails.purity}`
                          : ""}
                      </span>
                      <span style={{ fontWeight: 600, color: accent }}>
                        −{" "}
                        {formatCurrency(
                          exItem.agreedValue ??
                            exItem.systemAppraisal?.finalValue,
                        )}
                      </span>
                    </div>
                  ))}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "8px",
                      fontWeight: 700,
                      color: accent,
                      fontSize: "13px",
                    }}
                  >
                    <span>Total Exchange Value</span>
                    <span>− {formatCurrency(exchangeValue)}</span>
                  </div>
                </div>
              )}

              {/* Totals */}
              <div style={css.totalsRow}>
                <div style={css.totalsBox}>
                  {itemTotals.metalValue > 0 && (
                    <div style={css.totalLine}>
                      <span>Metal Value</span>
                      <span>{formatCurrency(itemTotals.metalValue)}</span>
                    </div>
                  )}
                  {itemTotals.makingCharges > 0 && (
                    <div style={css.totalLine}>
                      <span>Making Charges</span>
                      <span>{formatCurrency(itemTotals.makingCharges)}</span>
                    </div>
                  )}
                  {itemTotals.wastageCharges > 0 && (
                    <div style={css.totalLine}>
                      <span>Wastage Charges</span>
                      <span>{formatCurrency(itemTotals.wastageCharges)}</span>
                    </div>
                  )}
                  {itemTotals.stoneCharges > 0 && (
                    <div style={css.totalLine}>
                      <span>Stone Charges</span>
                      <span>{formatCurrency(itemTotals.stoneCharges)}</span>
                    </div>
                  )}
                  {itemTotals.subtotal > 0 && (
                    <div style={{ ...css.totalLine, fontWeight: 500 }}>
                      <span>Subtotal</span>
                      <span>{formatCurrency(itemTotals.subtotal)}</span>
                    </div>
                  )}
                  {totalDiscount > 0 && (
                    <div style={{ ...css.totalLine, color: "#16a34a" }}>
                      <span>Discount</span>
                      <span>− {formatCurrency(totalDiscount)}</span>
                    </div>
                  )}
                  {totalGST > 0 && (
                    <div style={css.totalLine}>
                      <span>GST (3%)</span>
                      <span>{formatCurrency(totalGST)}</span>
                    </div>
                  )}
                  <div style={css.totalLineGrand}>
                    <span>Grand Total</span>
                    <span>{formatCurrency(grandTotal)}</span>
                  </div>
                  {exchangeValue > 0 && (
                    <div
                      style={{
                        ...css.totalLine,
                        marginTop: "4px",
                        color: accent,
                        fontWeight: 500,
                      }}
                    >
                      <span>Less: Exchange</span>
                      <span>− {formatCurrency(exchangeValue)}</span>
                    </div>
                  )}
                  {exchangeValue > 0 && (
                    <div style={{ ...css.totalLine, fontWeight: 600 }}>
                      <span>Net Payable</span>
                      <span>{formatCurrency(netPayable)}</span>
                    </div>
                  )}
                  {amountPaid > 0 && (
                    <div
                      style={{
                        ...css.totalLine,
                        color: "#16a34a",
                        fontWeight: 500,
                      }}
                    >
                      <span>
                        Amount Paid{" "}
                        {order?.payment?.method
                          ? `(${order.payment.method})`
                          : ""}
                      </span>
                      <span>− {formatCurrency(amountPaid)}</span>
                    </div>
                  )}
                  <div style={css.totalLineDue}>
                    <span>{amountDue > 0 ? "Balance Due" : "Settled ✓"}</span>
                    <span>
                      {amountDue > 0
                        ? formatCurrency(amountDue)
                        : formatCurrency(0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment summary row */}
              {(summary.amountPaid !== undefined || order?.payment?.status) && (
                <div style={css.paymentGrid}>
                  <div style={css.payCell}>
                    <span style={css.payCellLabel}>Total Amount</span>
                    <span style={css.payCellValue}>
                      {formatCurrency(grandTotal)}
                    </span>
                  </div>
                  <div style={css.payCell}>
                    <span style={css.payCellLabel}>Paid</span>
                    <span style={{ ...css.payCellValue, color: "#16a34a" }}>
                      {formatCurrency(amountPaid)}
                    </span>
                  </div>
                  <div style={css.payCell}>
                    <span style={css.payCellLabel}>Balance Due</span>
                    <span
                      style={{
                        ...css.payCellValue,
                        color: amountDue > 0 ? "#dc2626" : "#16a34a",
                      }}
                    >
                      {amountDue > 0 ? formatCurrency(amountDue) : "Nil"}
                    </span>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div
                style={{
                  borderTop: `2px solid ${primary}`,
                  marginTop: isMobile ? "14px" : "20px",
                  paddingTop: "10px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "6px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: primary,
                    fontFamily: "'Cormorant Garamond', serif",
                    fontStyle: "italic",
                    fontWeight: 600,
                  }}
                >
                  Thank you for shopping with {storeName}
                </div>
                <div
                  style={{
                    fontSize: "9px",
                    color: "#bbb",
                    letterSpacing: "0.3px",
                  }}
                >
                  Computer-generated · No signature required ·{" "}
                  {formatDate(new Date().toISOString())}
                </div>
              </div>
            </div>
            {/* end paper */}
          </div>
        )}{" "}
        {/* end isBranchLoading ternary */}
      </div>
    </>
  );
}
